import React, { useMemo, useState } from 'react';
import { SessionsDetailModal } from '@medflex/components/stories/Sessions';
import buildContactData from '@medflex/utils/buildContactData';
import buildModerators from '@medflex/utils/buildModerators';
import commons from '@medflex/commons';
import { showAlertToast, showSuccessToast } from '@medflex/components/stories/Toast';
import { useTranslation } from 'react-i18next';
import config from '@medflex/utils/config';
import { trackEvent } from '@medflex/utils/Tracking';
import { dayjs } from '@medflex/shared-utils';
import { SubscriptionStatus } from '@medflex/shared-constants';
import isPastMeeting from '@medflex/utils/isPastMeeting';
import useSessionMeetings from '../hooks/useSessionMeetings';
import useSessionRoles from '../hooks/useSessionRoles';
import useUserProfile from '../hooks/useUserProfile';
import useAddressBookContacts from '../hooks/useAddressBookContacts';
import useModal from '../hooks/useModal';
import useLocationSearchParams from '../../hooks/useLocationSearchParams';
import useSnippets from '../hooks/useSnippets';
import getParticipantsWithoutOrganizer from '../../utils/getParticipantsWithoutOrganizer';
import useSessionMeeting from '../hooks/useSessionMeeting';
import { MODALS } from '../hooks/useIsModal';
import useSubscription from '../hooks/useSubscription';

const handleTrackEvent = (name, event) => {
  trackEvent(event, {
    zone: 'SessionsDetailModal',
    actionText: name,
  });
};

const { SESSIONS_URL } = config;
const SessionsDetailModalModule = () => {
  const [submitting, setSubmitting] = useState(false);
  const { orgaPhysicians, pairedPatients, pairedPhysicians, staff } = useAddressBookContacts();
  const { sessionId } = useLocationSearchParams();
  const { close, open } = useModal();
  const { snippets } = useSnippets();
  const { userProfile } = useUserProfile();
  const { t } = useTranslation('physician');
  const { meeting = {} } = useSessionMeeting({ id: sessionId });
  const { update, addParticipant, changeModerator } = useSessionMeetings();
  const moderators = useMemo(() => buildModerators(userProfile, staff), [userProfile, staff]);
  const { contractStatus, trialEndedAt, upgradeUrl } = useSubscription();

  const canSendSms =
    contractStatus === SubscriptionStatus.ACTIVE &&
    (!trialEndedAt || dayjs().isAfter(dayjs(trialEndedAt)));

  const { title, participants, scheduledAt, scheduledEndedAt, description } = meeting;

  const { moderator, isModerator, isOrganizer } = useSessionRoles(participants);
  const participantsWithoutOrganizer = useMemo(() => {
    if (participants) {
      return getParticipantsWithoutOrganizer(participants);
    }
    return [];
  }, [participants]);

  const invitees =
    moderator &&
    participantsWithoutOrganizer &&
    participantsWithoutOrganizer.filter(p => p.participantId !== moderator.participantId);

  const isPast = isPastMeeting(meeting);

  const chatUrl = useMemo(() => {
    if (userProfile && isPast && participantsWithoutOrganizer.length > 0) {
      const participant = participantsWithoutOrganizer.find(p => p.userId === userProfile.id);
      return participant
        ? `${SESSIONS_URL}/${sessionId}/${participant.participantId}/chat?tan=${participant.tan}`
        : null;
    }
    return null;
  }, [isPast, userProfile, participantsWithoutOrganizer]);

  const handleClose = () => {
    close({ modal: MODALS.sessionsDetail, sessionId });
  };

  const contacts = useMemo(() => {
    if (
      pairedPatients &&
      pairedPhysicians &&
      orgaPhysicians &&
      staff &&
      userProfile &&
      participants
    ) {
      const participantUserIds = participants.filter(p => p.userId).map(p => p.userId);
      const data = [...pairedPatients, ...pairedPhysicians, ...orgaPhysicians, ...staff].filter(
        c => !c.mainContactId,
      );
      const contactData = buildContactData(data, userProfile.id);
      return contactData.filter(({ userId }) => !participantUserIds.includes(userId));
    }
    return [];
  }, [pairedPatients, pairedPhysicians, orgaPhysicians, staff, userProfile, participants]);

  const handleUpdateData = async (data, event) => {
    try {
      if (!submitting) {
        setSubmitting(true);
        await update({ ...data, sessionId });
        handleTrackEvent('update data', event);
        setSubmitting(false);
        showSuccessToast(t('SessionsDetailModalModule.update_data.success'), null, {
          toastId: 'sessionDetailUpdateSuccess',
        });
        handleClose();
      }
    } catch {
      setSubmitting(false);
      showAlertToast(t('toasts.generic_failure.title'), t('toasts.generic_failure.text'), {
        toastId: 'sessionDetailUpdateFailed',
      });
    }
  };

  const handleUpdateModerator = async ({ newModeratorId }, event) => {
    if (submitting || newModeratorId === moderator.userId) {
      return;
    }

    try {
      setSubmitting(true);
      const data = {
        sessionId,
        moderatorId: newModeratorId,
      };
      await changeModerator(data);
      handleTrackEvent('change moderator', event);
      setSubmitting(false);
      showSuccessToast(t('SessionsDetailModalModule.update_data.success'), null, {
        toastId: 'sessionDetailUpdateSuccess',
      });
      handleClose();
    } catch {
      setSubmitting(false);
      showAlertToast(t('toasts.generic_failure.title'), t('toasts.generic_failure.text'), {
        toastId: 'sessionDetailUpdateFailed',
      });
    }
  };

  const handleDeleteParticipant = ({ participantId }) => {
    open({ modal: 'sessionsRemoveParticipant', participantId });
  };

  const hasPhoneParticipants = useMemo(() => {
    if (participants && participants.length) {
      return !!participants.find(({ referenceType }) => referenceType && referenceType === 'phone');
    }
    return false;
  }, [participants]);

  const handleAddParticipant = async (
    { userId, firstName, lastName, referenceType, reference },
    event,
  ) => {
    try {
      if (!submitting) {
        if (participants.find(p => p.reference && p.reference === reference)) {
          return showAlertToast(
            t('SessionsDetailModalModule.add_participant.already_participant'),
            '',
            { toastId: 'sessionaddParticipantFailed' },
          );
        }

        const body = userId
          ? { userId }
          : {
              firstName,
              lastName,
              reference:
                referenceType === 'phone'
                  ? commons.phoneNumber.transformToE164(reference).phoneNumberE164
                  : reference,
              referenceType,
            };
        setSubmitting(true);
        await addParticipant({ sessionId, participant: body });
        handleTrackEvent('addParticipant', event);
        showSuccessToast(t('SessionsDetailModalModule.add_participant.success'), null, {
          toastId: 'SessionAddParticipantSuccess',
        });
        setSubmitting(false);
        return handleClose();
      }

      return null;
    } catch {
      setSubmitting(false);
      return showAlertToast(t('toasts.generic_failure.title'), t('toasts.generic_failure.text'), {
        toastId: 'SessionAddParticipantFailed',
      });
    }
  };

  const isEditable = (isModerator || isOrganizer) && !isPast;

  return (
    <SessionsDetailModal onClose={handleClose}>
      <SessionsDetailModal.Moderator
        participants={participants}
        moderator={moderator}
        moderators={moderators}
        onSubmit={handleUpdateModerator}
        isEditable={isEditable}
        titlel={t('components:Sessions.SessionsDetailModal.SessionsDetailModalModerator.title')}
        disabled={submitting}
      />
      <SessionsDetailModal.Invitees
        contacts={contacts}
        onDelete={handleDeleteParticipant}
        onSubmit={handleAddParticipant}
        isEditable={isEditable}
        maxParticipants={9}
        title={t('components:Sessions.SessionsDetailModal.SessionsDetailModalInvitees.title')}
        invitees={invitees}
        disabled={submitting}
        canSendSms={canSendSms}
        smsUpgradeUrl={upgradeUrl}
      />
      <SessionsDetailModal.DataDisplay
        onSubmit={handleUpdateData}
        isEditable={isEditable}
        scheduledAt={scheduledAt}
        disabled={submitting}
        title={title}
        description={description}
        scheduledEndedAt={scheduledEndedAt}
        hasPhoneParticipants={hasPhoneParticipants}
        snippets={snippets}
      />
      {meeting.callId && (
        <span className="text-slate-500 mb-4">
          {t('components:Sessions.SessionsDetailModal.callId')}
          {meeting.callId}
        </span>
      )}
    </SessionsDetailModal>
  );
};

export default SessionsDetailModalModule;
